<template>
  <b-row no-gutters>
    <b-col lg="10">
      <div class="panel">
        <loader :watch="['licenceAreaSettings', 'projects']">
          <b-row class="mb-4">
            <b-col>
              <h2 class="mq">Licence Areas assignment</h2>
            </b-col>
          </b-row>
          <b-form-group>
            <b-form-radio-group
              v-if="filterLicenceAreas"
              v-model="filterLicenceAreas"
              :options="options"
              stacked
              name="radiosStacked"
              @change="changeFilter"
            />
          </b-form-group>
          <div v-if="Boolean(filteredList) && filterLicenceAreas !== 'all'">
            <div v-for="(project, index) in filteredList" :key="index" class="striped-list">
              <h3>
                <b-form-checkbox
                  v-model="project.selected"
                  @change="checked => toggleAll(checked, project.code)"
                >
                  {{ project.name || project.code }}
                </b-form-checkbox>
              </h3>
              <LicenceArea
                v-for="licenceArea in project.licenceAreas"
                :key="licenceArea.id"
                v-model="licenceArea.selected"
                :licence-area="licenceArea"
              />
            </div>
          </div>
          <b-form-group class="form-group__buttons">
            <router-link
              class="btn btn-link font-weight-bold"
              variant="link"
              :to="{ name: 'adminEditUser', params: { userId } }"
            >
              Cancel
            </router-link>
            <b-button type="button" variant="rounded" @click="save">
              Save
              <Icon name="save" />
            </b-button>
          </b-form-group>
        </loader>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import forOwn from 'lodash/forOwn';
import filter from 'lodash/filter';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import LicenceArea from './licence-area';

const FILTER_LICENCE_AREAS = {
  all: 'all',
  selected: 'selected'
};

export default {
  name: 'LicenceAreasList',
  components: { LicenceArea },
  props: ['userId'],
  data() {
    return {
      filterLicenceAreas: null,
      filterLicenceAreasTypes: FILTER_LICENCE_AREAS,
      options: [
        { text: 'assign to all', value: FILTER_LICENCE_AREAS.all },
        { text: 'assign to selected areas', value: FILTER_LICENCE_AREAS.selected }
      ],
      projects: []
    };
  },
  computed: {
    ...mapGetters({
      userSelectedLicenceAreas: 'users/licenceAreaAndZones'
    }),
    ...mapState({
      assignedAllLicenceAreas: state =>
        state.users.user && state.users.user.assignedAllLicenceAreas,
      user: state => state.users.user,
      rawProjects: state => state.projects.projects
    }),
    userProjects() {
      if (this.user) {
        if (this.user.role !== 'superadmin') {
          const privilegesScope = this.user.privilegesScope.match(/\/(.*?)\//).pop();
          return filter(this.projects, { code: privilegesScope });
        }
        return this.projects;
      }

      return [];
    },
    filteredList() {
      if (this.userProjects && this.userSelectedLicenceAreas) {
        this.userProjects.map(project => {
          project.licenceAreas.map(licenceArea => {
            const selectedLicenceAreas = filter(this.userSelectedLicenceAreas, {
              projectCode: project.code,
              licenceAreaCode: licenceArea.code
            });

            if (!Object.prototype.hasOwnProperty.call(licenceArea, 'selected')) {
              this.$set(licenceArea, 'selected', !!selectedLicenceAreas.length);
            }

            licenceArea.zones.map(zone => {
              if (!Object.prototype.hasOwnProperty.call(zone, 'selected')) {
                this.$set(
                  zone,
                  'selected',
                  !!filter(selectedLicenceAreas, { zoneCode: zone.code }).length
                );
              }
              return zone;
            });

            return licenceArea;
          });

          this.$set(
            project,
            'selected',
            !!project.licenceAreas.filter(licenceArea => licenceArea.selected).length
          );

          return project;
        });

        return this.userProjects;
      }
      return {};
    }
  },
  watch: {
    rawProjects(projects) {
      this.projects = cloneDeep(projects);
    }
  },
  mounted() {
    if (!this.user) {
      this.fetchUserData();
    }
    if (typeof this.assignedAllLicenceAreas !== 'undefined') {
      this.setFilter(this.assignedAllLicenceAreas);
    }

    this.projects = cloneDeep(this.rawProjects);
  },
  methods: {
    fetchUserData() {
      this.$store.dispatch('users/fetchUserData', { userId: this.userId }).then(() => {
        this.setFilter();
      });
    },
    setFilter() {
      this.filterLicenceAreas = this.assignedAllLicenceAreas
        ? this.filterLicenceAreasTypes.all
        : this.filterLicenceAreasTypes.selected;
    },
    changeFilter(filterType) {
      if (filterType === this.filterLicenceAreasTypes.selected) {
        this.filteredList.forEach((project, index) => {
          this.$set(this.filteredList, index, { ...project, selected: true });
          this.toggleAll(true, this.filteredList[index].code);
        });
      }
    },
    toggleAll(checked, projectCode) {
      const project = find(this.filteredList, { code: projectCode });

      this.$set(project, 'selected', checked);
      project.licenceAreas.forEach((licenceArea, laIndex) => {
        this.$set(project.licenceAreas, laIndex, { ...licenceArea, selected: checked });

        licenceArea.zones.forEach((zone, zIndex) => {
          this.$set(project.licenceAreas[laIndex].zones, zIndex, { ...zone, selected: checked });
        });
      });
    },

    save() {
      const assignedAllLicenceAreas = this.filterLicenceAreas === this.filterLicenceAreasTypes.all;
      const payload = {
        assignedAllLicenceAreas,
        licenceAreaAndZones: []
      };

      if (!assignedAllLicenceAreas) {
        forOwn(this.filteredList, project => {
          if (project.selected) {
            project.licenceAreas.forEach(licenceArea => {
              if (licenceArea.selected) {
                const selectedZones = filter(licenceArea.zones, { selected: true });
                const defaultData = {
                  projectCode: project.code,
                  licenceAreaCode: licenceArea.code
                };

                if (selectedZones.length) {
                  selectedZones.forEach(zone => {
                    if (zone.selected) {
                      payload.licenceAreaAndZones.push({
                        ...defaultData,
                        zoneCode: zone.code
                      });
                    }
                  });
                } else {
                  payload.licenceAreaAndZones.push({
                    ...defaultData,
                    zoneCode: ''
                  });
                }
              }
            });
          }
        });
      }

      this.$store
        .dispatch('licenceAreas/updateSettings', {
          ...payload,
          userId: this.userId
        })
        .then(() => {
          this.$router.push({ name: 'adminEditUser', params: { userId: this.userId } });
        });
    }
  }
};
</script>
